import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";

const Ctx = createContext(null);
export const useStore = () => useContext(Ctx);

const load = (k, def) => {
  try {
    return JSON.parse(localStorage.getItem(k)) ?? def;
  } catch (e) {
    return def;
  }
};
const cartId = () => {
  let id = localStorage.getItem("e3_cart_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("e3_cart_id", id);
  }
  return id;
};
const keyOf = (i) => JSON.stringify([i.product_id, i.options, i.colors, i.custom]);

export function StoreProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [customer, setCustomer] = useState(undefined);
  const [cart, setCart] = useState(() => load("e3_cart", []));
  const [favorites, setFavorites] = useState(() => load("e3_favs", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    api.get("/store/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    api.get("/auth/me").then((r) => setCustomer(r.data)).catch(() => setCustomer(null));
  }, []);

  useEffect(() => {
    localStorage.setItem("e3_cart", JSON.stringify(cart));
    const email = customer?.email || localStorage.getItem("e3_email");
    if (email) {
      api.post("/cart/sync", { cart_id: cartId(), email, items: cart.map((i) => ({ name: i.name, qty: i.qty, price: i.unit_price, product_id: i.product_id })) }).catch(() => {});
    }
  }, [cart, customer]);

  useEffect(() => {
    localStorage.setItem("e3_favs", JSON.stringify(favorites));
  }, [favorites]);

  const onLogin = useCallback(async (c) => {
    setCustomer(c);
    const merged = [...new Set([...(c.favorites || []), ...load("e3_favs", [])])];
    setFavorites(merged);
    api.put("/account/favorites", { favorites: merged }).catch(() => {});
  }, []);

  const logout = async () => {
    await api.post("/auth/logout");
    setCustomer(null);
  };

  const addToCart = (item, quiet = false) => {
    setCart((c) => {
      const k = keyOf(item);
      const ex = c.find((i) => keyOf(i) === k);
      return ex ? c.map((i) => (keyOf(i) === k ? { ...i, qty: i.qty + item.qty } : i)) : [...c, { ...item, key: crypto.randomUUID() }];
    });
    track("AddToCart", { content_ids: [item.product_id], value: item.unit_price * item.qty, currency: "BRL" });
    toast.success("Adicionado ao carrinho", { description: item.name });
    if (!quiet) setCartOpen(true);
  };
  const updateQty = (key, qty) => setCart((c) => c.map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i)));
  const removeItem = (key) => setCart((c) => c.filter((i) => i.key !== key));
  const clearCart = () => { localStorage.removeItem("e3_cart_id"); setCart([]); };

  const toggleFav = (id) => {
    setFavorites((f) => {
      const next = f.includes(id) ? f.filter((x) => x !== id) : [...f, id];
      if (customer) api.put("/account/favorites", { favorites: next }).catch(() => {});
      return next;
    });
  };

  const value = useMemo(() => ({
    settings, customer, setCustomer: onLogin, logout, cart, addToCart, updateQty, removeItem, clearCart, cartId,
    cartCount: cart.reduce((s, i) => s + i.qty, 0), favorites, toggleFav, cartOpen, setCartOpen,
  }), [settings, customer, cart, favorites, cartOpen, onLogin]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
